import React from 'react';
import Sketch from 'react-p5';

const FlangerDemo = () => {
  let lfoPhase = 0;
  const lfoSpeed = 0.02;
  const maxDelay = 20; // Maximum delay in "samples" (visual units)
  
  const setup = (p5, canvasParentRef) => {
    // Create canvas that fits the width of the parent container
    const width = Math.min(p5.windowWidth - 40, 800);
    p5.createCanvas(width, 400).parent(canvasParentRef);
    p5.textFont('Helvetica');
  };

  const windowResized = (p5) => {
    const width = Math.min(p5.windowWidth - 40, 800);
    p5.resizeCanvas(width, 400);
  };

  const draw = (p5) => {
    p5.background(26, 26, 26); // Match project background #1a1a1a
    
    // Calculate current delay based on LFO
    // LFO oscillates between 0 and 1
    const lfoValue = (Math.sin(lfoPhase) + 1) / 2;
    const currentDelay = p5.map(lfoValue, 0, 1, 0, maxDelay);
    
    // Update LFO phase
    lfoPhase += lfoSpeed;
    
    // --- TOP SECTION: TIME DOMAIN (Sine Waves) ---
    const topHeight = 180;
    const amplitude = 40;
    const frequency = 0.05;
    const centerY = topHeight / 2;
    
    // Draw labels
    p5.noStroke();
    p5.fill(255);
    p5.textSize(14);
    p5.textAlign(p5.LEFT, p5.TOP);
    p5.text("Time Domain: Dry (White) vs. Delayed (Blue)", 10, 10);
    
    // Draw Dry Signal (White)
    p5.stroke(255);
    p5.strokeWeight(2);
    p5.noFill();
    p5.beginShape();
    for (let x = 0; x < p5.width; x++) {
      const y = centerY + Math.sin(x * frequency + p5.frameCount * 0.05) * amplitude;
      p5.vertex(x, y);
    }
    p5.endShape();
    
    // Draw Wet Signal (Blue) - Delayed
    p5.stroke(0, 150, 255);
    p5.strokeWeight(2);
    p5.noFill();
    p5.beginShape();
    for (let x = 0; x < p5.width; x++) {
      // The "delay" here is a phase shift in the visual representation
      // In audio, delay is time-based. Here, x represents time.
      // So a delay means we are looking at the signal from 'currentDelay' pixels ago.
      // However, for a visual comparison of two waves "playing together", 
      // the delayed signal is just the dry signal shifted to the right.
      // So we subtract delay from x to get the value at that point? 
      // Actually, if signal is delayed, it arrives LATER. 
      // So at time t, we hear signal(t - delay).
      // So we calculate sin((x - currentDelay * 5) * frequency ...) 
      // Multiplier 5 to make the visual shift more obvious
      const delayPixels = currentDelay * 5; 
      const y = centerY + Math.sin((x - delayPixels) * frequency + p5.frameCount * 0.05) * amplitude;
      p5.vertex(x, y);
    }
    p5.endShape();
    
    // Draw Delay Indicator
    // Show the delay amount visually
    p5.noStroke();
    p5.fill(255, 255, 255, 100);
    p5.textAlign(p5.RIGHT, p5.CENTER);
    p5.text(`Delay: ${currentDelay.toFixed(1)} ms`, p5.width - 10, 20);
    
    
    // --- BOTTOM SECTION: FREQUENCY DOMAIN (Comb Filter) ---
    const bottomTop = 220;
    const bottomHeight = 180;
    const bottomCenterY = bottomTop + bottomHeight;
    
    // Draw separator line
    p5.stroke(50);
    p5.strokeWeight(1);
    p5.line(0, 200, p5.width, 200);
    
    // Draw labels
    p5.noStroke();
    p5.fill(255);
    p5.textAlign(p5.LEFT, p5.TOP);
    p5.text("Frequency Domain: Comb Filtering Effect", 10, 210);
    
    // Draw Comb Filter Response
    // Magnitude response |H(f)| = |cos(pi * f * delay)|
    // We map x to frequency f
    p5.stroke(255, 100, 100);
    p5.strokeWeight(2);
    p5.noFill();
    p5.beginShape();
    
    for (let x = 0; x < p5.width; x++) {
      // Map x to frequency (0 to ~20kHz conceptually, but normalized here)
      // Let's say x goes from 0 to 1 (normalized frequency)
      const normFreq = x / p5.width;
      
      // Calculate magnitude
      // delay is in "samples". 
      // If delay is 0, response is flat (constructive interference everywhere if in phase, but flanger usually starts at small delay)
      // If delay is small, notches are far apart.
      // If delay is large, notches are close together.
      
      // We need to scale the delay to make the visual interesting
      // Visual delay range 0-20. 
      // Avoid delay = 0 for the formula (cos(0)=1, flat line)
      const effectiveDelay = Math.max(0.1, currentDelay);
      
      // The formula for comb filter magnitude (feedforward)
      // |H(f)| = |2 * cos(pi * f * delay)|
      // We normalize amplitude to fit in the box
      const magnitude = Math.abs(Math.cos(Math.PI * normFreq * effectiveDelay * 2)); 
      
      // Map magnitude (0 to 1) to y position
      // 0 magnitude -> bottom (bottomCenterY)
      // 1 magnitude -> top (bottomTop + 20 padding)
      const y = p5.map(magnitude, 0, 1, bottomCenterY, bottomTop + 40);
      
      p5.vertex(x, y);
    }
    p5.endShape();
    
    // Draw frequency axis label
    p5.noStroke();
    p5.fill(150);
    p5.textSize(10);
    p5.textAlign(p5.CENTER, p5.BOTTOM);
    p5.text("Frequency (Low -> High)", p5.width / 2, p5.height - 5);
  };

  return (
    <div className="flanger-demo-container" style={{ margin: '2rem 0', display: 'flex', justifyContent: 'center' }}>
      <Sketch setup={setup} draw={draw} windowResized={windowResized} />
    </div>
  );
};

export default FlangerDemo;
